import { Cylinder, Edges } from '@react-three/drei'
import { ThreeEvent } from '@react-three/fiber'
import { formatForLabel } from 'features/siteMap/communalMapStyling/FormatForLabel'
import { useTilesetMapFeatureHelpers } from 'features/siteMap/tilesetMap/features/useTilesetMapFeatureHelpers'
import { TilesetMapFeatureLabel } from 'features/siteMap/tilesetMap/objects/TilesetMapFeatureLabel'
import {
  MapFeaturePattern,
  TilesetMapFeatureStandardMaterial,
} from 'features/siteMapViz'
import React, { ReactNode, useState } from 'react'
import { SiteFeaturePrimitiveCylinder } from 'types'

import { convertRotationToRadians } from './utils'

export const TilesetMapFeatureCylinder3D: React.FC<{
  cylinder: SiteFeaturePrimitiveCylinder
  name: string | undefined
  color: string
  visible: boolean
  pattern: MapFeaturePattern
  onClick: (e: ThreeEvent<MouseEvent>) => void
  extraLabelDisplay?: ReactNode
}> = ({
  cylinder,
  name,
  color,
  visible,
  pattern,
  extraLabelDisplay,
  onClick,
}) => {
  const { convertCartographicToPosition, getFeatureOpacity } =
    useTilesetMapFeatureHelpers()

  const [showName, setShowName] = useState(false)
  const xyz = React.useMemo(
    () => convertCartographicToPosition(cylinder.coordinate),
    [convertCartographicToPosition, cylinder.coordinate]
  )
  const opacity = getFeatureOpacity(cylinder)

  return (
    <group position={xyz} visible={visible}>
      <Cylinder
        name={cylinder.id}
        userData={{ siteFeature: cylinder, uid: cylinder.id }}
        onPointerMove={(e) => {
          setShowName(
            e.intersections.at(0)?.eventObject.userData.uid === cylinder.id
          )
        }}
        onPointerLeave={() => setShowName(false)}
        rotation={convertRotationToRadians(cylinder.rotation)}
        args={[
          cylinder.geometry.top_radius,
          cylinder.geometry.bottom_radius,
          cylinder.geometry.height,
          undefined,
          undefined,
          undefined,
          cylinder.geometry.theta_start === null
            ? undefined
            : cylinder.geometry.theta_start,
          cylinder.geometry.theta_end === null
            ? undefined
            : cylinder.geometry.theta_end,
        ]}
        onClick={onClick}>
        <TilesetMapFeatureStandardMaterial
          color={color}
          baseOpacity={opacity}
          pattern={pattern}
          importantFaceLength={cylinder.geometry.top_radius}
        />
        <Edges
          color={'white'}
          lineWidth={0}
          transparent={true}
          opacity={opacity}
          polygonOffset={true}
          polygonOffsetFactor={-7}
          polygonOffsetUnits={-7}
        />
      </Cylinder>
      {!name ? null : (
        <TilesetMapFeatureLabel
          body={formatForLabel(name, extraLabelDisplay)}
          hovered={showName}
          visible={visible}
        />
      )}
    </group>
  )
}
